import { useQuery } from '@tanstack/react-query';
import { getOrderDetails } from '@/api/getOrderDetails';
import { getTableDetails } from '@/api/getTableDetails';
import { queryKeys } from '@/constants/queryKeys';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import { useLocale } from './useLocale';

interface Params {
  amount?: number;
  selectedTipPercentage?: number;
  enabled?: boolean;
  paused?: boolean;
}

export const useBill = (params?: Params) => {
  const { queryParams } = useValidateQueryParams();
  const { outletId, tableNumber, counterNumber } = queryParams();
  const locale = useLocale();

  const isEnabled = params?.enabled !== false;
  const paused = params?.paused === true;
  const selectedTipPercentage = params?.selectedTipPercentage;
  const amount = params?.amount && params.amount > 0 ? params.amount : undefined;

  if (!tableNumber && !counterNumber) {
    throw new Error('Counter number or table number are required');
  }

  if (!outletId) {
    throw new Error('Outlet ID is required');
  }

  return useQuery({
    queryKey: queryKeys.bill({
      outletId,
      tableNumber,
      amount,
      selectedTipPercentage,
      locale,
    }),
    queryFn: ({ signal }) => {
      if (counterNumber) {
        return getOrderDetails({
          outletId,
          amount,
          counterNumber,
          selectedTipPercentage,
          signal,
          locale,
        });
      }
      if (tableNumber) {
        return getTableDetails({
          outletId,
          amount,
          tableNumber,
          selectedTipPercentage,
          signal,
          locale,
        });
      }
      throw new Error('Counter number or table number are required');
    },
    staleTime: 0,
    enabled: isEnabled && !paused && !!outletId && (!!tableNumber || !!counterNumber),
    refetchInterval: paused ? false : 10000,
  });
};
